import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Camera, Eye, FileDown, Loader2, Save, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, downloadFile, formatErr } from "@/lib/api";

export function Milestones({ d, onChange }) {
  const [day, setDay] = useState(d.milestones?.signing_date || "");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/lease-loi/${d.loi_id}/milestones`, { signing_date: day }); toast.success("Lease milestones added to your Compliance Calendar."); onChange(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="loi-milestones">
      <p className="text-sm text-slate-600">Enter your expected lease signing date. We work out the contingency deadline, permit targets and when free rent ends from this LOI's numbers.</p>
      <div className="flex gap-2 flex-wrap"><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-10 w-48" data-testid="loi-milestones-date" />
        <Button onClick={go} disabled={busy || !day} className="h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="loi-milestones-save">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-1" />}{d.milestones ? "Update calendar" : "Add to calendar"}</Button></div>
      {d.milestones && <ul className="text-sm divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="loi-milestones-list">{d.milestones.dates.map((m) => (
        <li key={m.title} className="flex justify-between gap-3 px-3 py-2"><span className="text-slate-700">{m.title}</span><span className="font-semibold text-slate-900 whitespace-nowrap">{new Date(`${m.date}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span></li>))}</ul>}
      {d.milestones && <Link to="/dashboard/calendar" className="text-xs text-emerald-700 hover:underline" data-testid="loi-milestones-calendar">Open Compliance Calendar</Link>}
    </div>
  );
}

function Photo({ d, p, onDelete }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/lease-loi/${d.loi_id}/tour/photos/${p.photo_id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [d.loi_id, p.photo_id]);
  return (
    <div className="relative h-20 w-20 rounded-md overflow-hidden bg-slate-100" data-testid={`tour-photo-${p.photo_id}`}>
      {url && <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="Tour" className="h-full w-full object-cover" /></a>}
      <button onClick={() => window.confirm("Delete this photo?") && onDelete(p.photo_id)} className="absolute top-0 right-0 h-9 w-9 grid place-items-center" aria-label="Delete photo" data-testid={`tour-photo-delete-${p.photo_id}`}><span className="bg-black/60 text-white rounded-full p-1"><X className="h-3.5 w-3.5" /></span></button>
    </div>
  );
}

function PhotoButton({ d, i, onAdded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file); fd.append("section", String(i));
      try { await api.post(`/lease-loi/${d.loi_id}/tour/photos`, fd); } catch (err) { toast.error(formatErr(err)); break; }
    }
    setBusy(false); onAdded();
  };
  return (<>
    <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={up} data-testid={`tour-photo-input-${i}`} />
    <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="h-9" data-testid={`tour-photo-add-${i}`}>{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />}Photo</Button>
  </>);
}

function TourShare({ d }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", note: "" });
  const [busy, setBusy] = useState("");
  const dl = async () => { setBusy("dl"); try { await downloadFile(`/lease-loi/${d.loi_id}/tour/report.pdf`, "Site-Tour-Report.pdf"); } catch { toast.error("Couldn't build the report"); } finally { setBusy(""); } };
  const send = async () => {
    setBusy("send");
    try { await api.post(`/lease-loi/${d.loi_id}/tour/send`, f); toast.success(`Report link sent to ${f.email}`); setOpen(false); setF({ email: "", note: "" }); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="tour-share">
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={dl} disabled={!!busy} data-testid="tour-report-download">{busy === "dl" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}Tour report PDF</Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="tour-report-share-toggle"><Send className="h-4 w-4 mr-1" />Send to agent or architect</Button>
      </div>
      {open && <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="tour-report-share-form">
        <Input type="email" placeholder="Their email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="tour-report-email" />
        <Textarea rows={2} placeholder="Add a note (optional)" maxLength={1500} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="tour-report-note" />
        <Button size="sm" onClick={send} disabled={!!busy || !f.email} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tour-report-send">{busy === "send" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Send secure link</Button>
      </div>}
    </div>
  );
}

export function TourMode({ d, onChange }) {
  const [checks, setChecks] = useState(d.tour?.checks || {});
  const [notes, setNotes] = useState(d.tour?.notes || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const timer = useRef(null);
  const pending = useRef(null);
  const secs = d.checklist?.sections || [];
  const total = secs.reduce((a, s) => a + s.items.length, 0);
  const done = Object.values(checks).filter(Boolean).length;
  const save = async (c, n) => { pending.current = null; setSaving(true); try { await api.put(`/lease-loi/${d.loi_id}/tour`, { checks: c, notes: n }); setDirty(false); onChange(); } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); } };
  const queue = (c, n) => { clearTimeout(timer.current); setDirty(true); pending.current = [c, n]; timer.current = setTimeout(() => save(c, n), 800); };
  useEffect(() => () => {
    clearTimeout(timer.current);
    if (pending.current) api.put(`/lease-loi/${d.loi_id}/tour`, { checks: pending.current[0], notes: pending.current[1] }).then(onChange).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (d.checklist_status !== "ready") return <p className="text-sm text-slate-500" data-testid="tour-needs-checklist">Make the site checklist first (Site checklist tab), then tick it off here during your tour.</p>;
  const delPhoto = (pid) => api.delete(`/lease-loi/${d.loi_id}/tour/photos/${pid}`).then(onChange).catch((e) => toast.error(formatErr(e)));
  return (
    <div className="space-y-4" data-testid="tour-mode">
      <TourShare d={d} />
      <div className="flex items-center gap-3 sticky top-14 lg:top-0 bg-white py-2 z-30"><div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
        <span className="text-xs text-slate-600 whitespace-nowrap" data-testid="tour-score">{done}/{total} · {total ? Math.round((done / total) * 100) : 0}%</span>
        <span className="text-xs text-slate-400 inline-flex items-center gap-1" data-testid="tour-save-state">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}{saving ? "Saving" : dirty ? "Unsaved" : "Saved"}</span></div>
      {secs.map((s, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`tour-section-${i}`}>
          <div className="flex items-center gap-2"><h4 className="font-semibold text-sm text-slate-900 flex-1">{s.heading}</h4><PhotoButton d={d} i={i} onAdded={onChange} /></div>
          <ul className="mt-2 space-y-2">{s.items.map((x, j) => { const k = `s${i}-${j}`; return (
            <li key={k}><label className="flex items-start gap-3 cursor-pointer min-h-[2rem]"><Checkbox checked={!!checks[k]} onCheckedChange={(v) => { const c = { ...checks, [k]: !!v }; setChecks(c); queue(c, notes); }} className="mt-0.5 h-5 w-5" data-testid={`tour-check-${k}`} /><span className={`text-sm ${checks[k] ? "text-slate-400 line-through" : "text-slate-700"}`}>{x}</span></label></li>); })}</ul>
          <Textarea rows={2} placeholder="Notes for this section" value={notes[`s${i}`] || ""} onChange={(e) => { const n = { ...notes, [`s${i}`]: e.target.value }; setNotes(n); queue(checks, n); }} className="mt-2 text-sm" data-testid={`tour-note-s${i}`} />
          {(d.tour?.photos || []).some((p) => p.section === i) && <div className="flex flex-wrap gap-2 mt-2">{d.tour.photos.filter((p) => p.section === i).map((p) => <Photo key={p.photo_id} d={d} p={p} onDelete={delPhoto} />)}</div>}
        </div>))}
    </div>
  );
}

export function AgentOpens({ d }) {
  if (!d.sends?.length) return null;
  const last = d.opens?.length ? d.opens[d.opens.length - 1] : null;
  return (
    <p className={`text-xs flex items-center gap-1.5 ${d.open_count ? "text-emerald-700" : "text-slate-500"}`} data-testid="loi-agent-opens"><Eye className="h-3.5 w-3.5" />
      {d.open_count ? `Opened ${d.open_count} time${d.open_count === 1 ? "" : "s"} · last ${new Date(last.at).toLocaleString()}` : "Not opened yet. We'll email you the first time your agent opens it."}</p>
  );
}
